import { MarketplacePropertyCategory_ListingMediaState as ListingMediaState } from '@opendoor/observability/pbs/journey/data/categories/marketplace_property_category_pb';

/**
 * Enhanced media a listing carries, as far as the marketplace read path can
 * report it. This is the only input to slot ownership: eligibility is a
 * property of the inventory, never of the viewer.
 */
export type ListingMediaAvailability = {
  hasWalkthroughVideo: boolean;
  hasTour3d: boolean;
};

/**
 * One experiment's claim on the PDP media slot.
 *
 * `armStates` maps an assigned arm to the state the slot renders for it. It is
 * a map rather than a boolean so a claim can add arms without the slot
 * learning about them, and an arm missing from it renders `SLOT_FALLBACK_STATE`
 * — an arm shipped in the platform ahead of the client degrades to photos
 * instead of rendering nothing.
 */
export type MediaSlotClaim = {
  readonly experimentKey: string;
  readonly claims: (availability: ListingMediaAvailability) => boolean;
  readonly armStates: Readonly<Record<string, ListingMediaState>>;
};

/** State the slot holds for an unassigned listing, and for an arm it cannot map. */
export const SLOT_FALLBACK_STATE = ListingMediaState.PHOTOS;

export const PDP_HERO_VIDEO_EXPERIMENT_KEY = 'resale-pdp-hero-walkthrough-video';
export const PDP_HERO_TOUR_3D_EXPERIMENT_KEY = 'resale-pdp-hero-3d-tour';

/**
 * Claims on the PDP media slot, in precedence order.
 *
 * The walkthrough-video test and the 3D-tour test draw from different
 * inventory but paint the same region of the page, so a listing carrying both
 * assets would enroll in two experiments at once and contaminate both
 * controls. The first claim whose inventory predicate matches owns the
 * listing; no later claim evaluates, assigns, or records an exposure for it.
 * That makes the eligible sets disjoint by construction rather than by
 * agreement between the two features.
 *
 * Video leads because its eligible set is the narrower one — pipeline output
 * in a handful of markets, against a tour bought on most listings. Ceding the
 * overlap the other way would spend most of the video test's inventory to gain
 * the tour test very little.
 *
 * A new claim is a new entry here, placed by how scarce its inventory is.
 * Nothing else needs to change: exclusivity, assignment, and the media state
 * all follow from this list.
 */
export const PDP_MEDIA_SLOT_CLAIMS: readonly MediaSlotClaim[] = [
  {
    experimentKey: PDP_HERO_VIDEO_EXPERIMENT_KEY,
    claims: (availability) => availability.hasWalkthroughVideo,
    armStates: {
      control: ListingMediaState.PHOTOS,
      treatment: ListingMediaState.VIDEO,
    },
  },
  {
    experimentKey: PDP_HERO_TOUR_3D_EXPERIMENT_KEY,
    claims: (availability) => availability.hasTour3d,
    armStates: {
      control: ListingMediaState.PHOTOS,
      treatment: ListingMediaState.TOUR_3D,
    },
  },
];

/** The claim that owns this listing's media slot, or null when none does. */
export const resolveMediaSlotClaim = (
  availability: ListingMediaAvailability,
  claims: readonly MediaSlotClaim[] = PDP_MEDIA_SLOT_CLAIMS,
): MediaSlotClaim | null => claims.find((claim) => claim.claims(availability)) ?? null;

/** State the slot renders for `arm`, falling back to photos for an unmapped or absent arm. */
export const resolveMediaState = (
  claim: MediaSlotClaim | null,
  arm: string | null,
): ListingMediaState => {
  if (!claim || arm == null) {
    return SLOT_FALLBACK_STATE;
  }
  // Own-property check, not a plain index: `arm` is an operator-chosen variation
  // name arriving from the platform, and a bare lookup for one that collides
  // with `Object.prototype` — `constructor`, `toString` — resolves to an
  // inherited function that `??` does not catch, returning a non-state where the
  // signature promises one.
  return Object.hasOwn(claim.armStates, arm)
    ? (claim.armStates[arm] ?? SLOT_FALLBACK_STATE)
    : SLOT_FALLBACK_STATE;
};

/** Baseline for a listing carrying no enhanced media, and the shape a claim's absence resolves to. */
export const NO_ENHANCED_MEDIA: ListingMediaAvailability = {
  hasWalkthroughVideo: false,
  hasTour3d: false,
};
